import type * as Highcharts from 'highcharts'
import React, { useEffect, useRef, useState } from 'react'

import { TextAnalyticsChartApi } from '../api/textAnalyticsApi'
import type { Sentiment } from '../api/types'
import { textAnalyticsBus } from '../bus/textAnalyticsBus'

import type { SelectionState } from './chartOptions'
import {
	buildSnapshotClickHandler,
	buildTrendClickHandler,
	buildMouseOutHandler,
	buildLoadHandler,
	buildTrendPointMouseOutHandler,
	buildTrendSeriesMouseOutHandler,
} from './chartOptions'
import { useHighchartsInstance } from './useHighchartsInstance'

declare global {
	interface Window {
		__taChartOptions?: Record<number, Highcharts.Options>
	}
}

type Props = {
	blockId: number
	endpoints: { click: string; data: string }
	// When true, this component subscribes directly to textAnalyticsBus for its own blockId (see
	// the effect below). Only the standalone/parent mount (bootstrap.ts's `text-analytics-chart`
	// mount) should pass true — a chart rendered as a linked child inside LinkedReportContainer
	// must NOT self-subscribe, because LinkedReportContainer already subscribes for that same
	// blockId; both subscribing would double-handle every bus event for the child. Defaults to
	// false so any caller that omits the prop is safe by default.
	selfSubscribe?: boolean
}

const installChartScript = (script: string, blockId: number): boolean => {
	try {
		const trimmed = script.trim()
		// The script may be either a bare options literal (e.g. "{...}") or a full assignment
		// statement (e.g. "window.__taChartOptions[3018] = ({...});"). Detect which.
		if (trimmed.startsWith('{') || trimmed.startsWith('(')) {
			const opts = new Function(`return (${trimmed})`)() as Highcharts.Options
			window.__taChartOptions = window.__taChartOptions ?? {}
			window.__taChartOptions[blockId] = opts
		} else {
			// Statement form — executing populates window.__taChartOptions for us.
			new Function(trimmed)()
		}
		return true
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error('[TextAnalyticsChart] failed to install chart script', e)
		return false
	}
}

type ChartCanvasProps = {
	blockId: number
	options: Highcharts.Options
}

// Rendered as a `key`-ed child of `TextAnalyticsChart` (see below) so that bumping `chartKey`
// forces a full unmount/remount of THIS component — and therefore a fresh `useHighchartsInstance`
// mount effect — rather than reusing the existing Highcharts instance.
//
// This indirection matters: hooks are bound to the fiber of the component that calls them, not
// to whatever host element that component happens to return. Putting `key={chartKey}` directly
// on the `<div>` that `TextAnalyticsChart` itself returns would only swap that div's DOM node; it
// would NOT reset `useHighchartsInstance`'s `useEffect(..., [])` (that effect lives on
// TextAnalyticsChart's own fiber, which never remounts), so the old chart would leak and the new
// div would stay empty forever. The key must sit at a component boundary, so it lives here,
// mirroring how `LinkedReportContainer` keys its `<TextAnalyticsChart key={chartKey} .../>`.
const ChartCanvas: React.FC<ChartCanvasProps> = ({ blockId, options }) => {
	const containerRef = useRef<HTMLDivElement | null>(null)
	useHighchartsInstance(containerRef, options)
	return <div ref={containerRef} className="h-full w-full" data-block-id={blockId} />
}

export const TextAnalyticsChart: React.FC<Props> = ({ blockId, endpoints, selfSubscribe = false }) => {
	const apiRef = useRef(new TextAnalyticsChartApi(endpoints))

	const selectionRef = useRef<SelectionState>({ categoryId: null, sentiment: null })
	// Re-render state used to trigger re-reads of selectionRef when selection changes.
	const [, setSelectionVersion] = useState(0)
	// Remount key bumped when a fresh chart script is installed via the bus (recolor/recompute).
	const [chartKey, setChartKey] = useState(0)

	const getSelection = (): SelectionState => selectionRef.current

	const handlePointClick = async (params: { categoryId: number; sentiment: Sentiment | null }): Promise<void> => {
		selectionRef.current = { categoryId: params.categoryId, sentiment: params.sentiment }
		setSelectionVersion((v) => v + 1)

		try {
			const response = await apiRef.current.click(params)
			response.linkedStates.forEach((state) => textAnalyticsBus.publish(state))
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('[TextAnalyticsChart] click failed', err)
		}
	}

	// Self-subscribe to the bus for this chart's own blockId. Local filter changes applied via
	// SnapshotChartControl signals publish a fresh `chart` state (with a re-rendered chartScript)
	// for the parent block alongside the child resets. Children are handled by their own
	// LinkedReportContainer subscription — subscribing here too would double-fire for them, so
	// this effect is gated by `selfSubscribe` and only the standalone/parent mount opts in (see
	// the `selfSubscribe` prop doc above).
	useEffect(() => {
		if (!selfSubscribe) {
			return
		}
		return textAnalyticsBus.subscribe(blockId, (next) => {
			if (next.displayType !== 'chart' || next.chartScript === undefined) {
				return
			}
			if (window.__taChartOptions !== undefined) {
				delete window.__taChartOptions[blockId]
			}
			if (installChartScript(next.chartScript, blockId)) {
				// Reset local selection highlight and remount Highcharts with fresh options.
				selectionRef.current = { categoryId: null, sentiment: null }
				setChartKey((k) => k + 1)
			}
		})
	}, [blockId, selfSubscribe])

	// Read the Highcharts options object emitted by the inline <script> tag next to this
	// mount point. The script populates window.__taChartOptions[blockId] before React mounts.
	// The options object contains real JS functions (formatters, event handlers) because the
	// browser evaluated the script tag — do NOT JSON.parse/JSON.stringify (that would kill functions).
	const baseOptions = window.__taChartOptions?.[blockId]
	if (baseOptions === undefined) {
		// eslint-disable-next-line no-console
		console.warn(`[TextAnalyticsChart] no initial options found for blockId ${blockId}`)
	}

	// Determine chart type from the base options (column = snapshot, line = trend).
	const isSnapshotChart = (baseOptions?.chart as { type?: string } | undefined)?.type === 'column'

	// We mutate the options tree in-place. The window registry entry is only ever used once
	// per blockId (the script tag fires once, React mounts once), so mutation is safe.
	// We must NOT use JSON.parse/JSON.stringify because that would strip the JS functions
	// (tooltip formatter, load/mouseOut event handlers) that BaseGraph embedded.
	const options: Highcharts.Options = baseOptions ?? {}

	// Ensure intermediate objects exist before assigning event handlers.
	options.plotOptions ??= {}
	options.plotOptions.series ??= {}
	options.plotOptions.series.point ??= {}
	options.plotOptions.series.point.events ??= {}
	options.chart ??= {}
	options.chart.events ??= {}

	if (isSnapshotChart) {
		// Snapshot variant: click reads categoryIds from chart.userOptions
		options.plotOptions.series.point.events.click = buildSnapshotClickHandler(
			handlePointClick,
		) as Highcharts.PointClickCallbackFunction
		options.plotOptions.series.point.events.mouseOut = buildMouseOutHandler(
			getSelection,
		) as Highcharts.PointMouseOutCallbackFunction
		options.chart.events.load = buildLoadHandler(getSelection) as Highcharts.ChartLoadCallbackFunction
	} else {
		// Trend variant: click reads categoryId from series.userOptions
		options.plotOptions.series.point.events.click = buildTrendClickHandler(
			handlePointClick,
		) as Highcharts.PointClickCallbackFunction
		options.plotOptions.series.point.events.mouseOut = buildTrendPointMouseOutHandler(
			getSelection,
		) as Highcharts.PointMouseOutCallbackFunction
		options.plotOptions.series.events ??= {}
		options.plotOptions.series.events.mouseOut = buildTrendSeriesMouseOutHandler(
			getSelection,
		) as Highcharts.SeriesMouseOutCallbackFunction
		options.chart.events.load = buildLoadHandler(getSelection) as Highcharts.ChartLoadCallbackFunction
	}

	return <ChartCanvas key={chartKey} blockId={blockId} options={options} />
}
